/**
 * AssistRuns — keeps at most one Review Assist run alive per shoot.
 *
 * It exists because a run has to outlive the page that started it. The popup is
 * answered on the shoot page and the photographer immediately goes off to cull;
 * if the run were owned by a React component it would be cancelled by that
 * navigation, and "you can keep culling while it works" would be a lie.
 *
 * Its only job is lifetime and subscription. It decides nothing about what a
 * review means — that is AssistService's, which it calls and otherwise leaves
 * alone.
 */

import type { AssistProgress, AssistService } from '../services';

type Listener = (progress: AssistProgress) => void;

interface Run {
  signal: { aborted: boolean };
  progress: AssistProgress;
  deep: boolean;
  /** Settles when this run is finished, cancelled or has failed. */
  finished: Promise<unknown>;
}

export class AssistRuns {
  private readonly runs = new Map<number, Run>();
  private readonly listeners = new Map<number, Set<Listener>>();

  constructor(
    private readonly assist: AssistService,
    /**
     * Makes the work durable now rather than on the next debounce.
     *
     * A finished review is minutes of someone's GPU. The snapshot is written on
     * a timer, so a tab closed in the wrong second would throw all of it away
     * and the next visit would start again from nothing.
     */
    private readonly flush: () => Promise<void>,
  ) {}

  /** The live progress of a run, or null when nothing is running for this shoot. */
  current(projectId: number): AssistProgress | null {
    return this.runs.get(projectId)?.progress ?? null;
  }

  /**
   * Begin a review, unless one is already under way for this shoot.
   *
   * Returns the promise so a caller that wants to wait can, but nothing needs
   * to: progress arrives through the subscription either way.
   */
  start(projectId: number, deep: boolean): Promise<AssistProgress> {
    const existing = this.runs.get(projectId);
    if (existing) {
      if (!deep || existing.deep) return Promise.resolve(existing.progress);

      // The sharpness pass starts on its own whenever the culling screen opens,
      // so by the time the photographer presses the button one is very often
      // already going. Handing them that run instead of the one they asked for
      // means the model never runs and the button appears to do nothing. Stop
      // the shallow pass and start the real one as soon as it unwinds.
      existing.signal.aborted = true;
      return existing.finished.then(() => this.start(projectId, true));
    }

    const signal = { aborted: false };
    const run: Run = {
      signal,
      deep,
      progress: { projectId, phase: 'scoring', done: 0, total: 0, failed: 0 },
      finished: Promise.resolve(),
    };
    this.runs.set(projectId, run);

    const finished = this.assist
      .review(projectId, {
        deep,
        signal,
        onProgress: (progress) => {
          run.progress = progress;
          this.emit(projectId, progress);
        },
      })
      .finally(() => {
        // Only clear if this is still the current run. A stop-then-start races
        // otherwise, and the new run would be forgotten the moment the old one
        // finished unwinding.
        if (this.runs.get(projectId) === run) this.runs.delete(projectId);
      })
      .finally(() => this.flush());

    run.finished = finished.catch(() => undefined);
    return finished;
  }

  stop(projectId: number): void {
    const run = this.runs.get(projectId);
    if (run) run.signal.aborted = true;
  }

  /** Stops everything. Called when the database is being torn down. */
  stopAll(): void {
    for (const run of this.runs.values()) run.signal.aborted = true;
  }

  /** Subscribe to one shoot's progress. Returns the unsubscribe. */
  subscribe(projectId: number, listener: Listener): () => void {
    const set = this.listeners.get(projectId) ?? new Set<Listener>();
    set.add(listener);
    this.listeners.set(projectId, set);

    return () => {
      set.delete(listener);
      if (set.size === 0) this.listeners.delete(projectId);
    };
  }

  private emit(projectId: number, progress: AssistProgress): void {
    for (const listener of this.listeners.get(projectId) ?? []) listener(progress);
  }
}
